import type { LegalDoc } from '../types';

// Transcribed 1:1 from "02 - Politica de Privacidad v1.0.pdf" (MEC Vault/legal/app-legal).
export const privacidad: LegalDoc = {
  slug: '/privacidad',
  title: 'Política de Privacidad',
  version: '1.0',
  updated: 'Julio de 2026',
  entity: 'Mi Envío Cuba',
  seoDescription:
    'Política de Privacidad de Mi Envío Cuba: qué información recopilamos, para qué la usamos, con quién la compartimos, cuánto tiempo la guardamos y sus derechos.',
  closing: 'Fecha de última actualización: Julio de 2026 · Versión: 1.0',
  sections: [
    {
      id: 'seccion-1',
      number: '1',
      title: 'Quiénes somos y qué es la Plataforma',
      blocks: [
        { t: 'h3', text: '1.1 Identidad y contacto' },
        {
          t: 'p',
          text: 'MEC LLC, que opera bajo el nombre comercial “Mi Envío Cuba” (“MEC”, “nosotros”), es una sociedad de responsabilidad limitada del estado de Florida, Estados Unidos. MEC opera una **plataforma de software** que conecta al Usuario con proveedores independientes de servicios en el corredor Estados Unidos — Cuba (agencias de envío, comerciantes cubanos, transitarias, tiendas externas).',
        },
        {
          t: 'ul',
          items: ['**Correo:** support@mienviocuba.com', '**Teléfono:** +1 (786) 519-1709'],
        },
        {
          t: 'p',
          text: 'Esta Política se aplica a la aplicación Mi Envío Cuba, al portal web mienviocuba.com, al Marketplace integrado, a los servicios Casillero Envíos y Casillero Tienda, y a los canales de soporte de MEC. No se aplica a las prácticas de los proveedores externos (Agencias, Comerciantes, transportistas, tiendas externas, procesadores) a los que enlazamos o con quienes usted transacta.',
        },
        { t: 'h3', text: '1.2 Rol de MEC — controlador y procesador según el flujo' },
        {
          t: 'p',
          text: 'Como plataforma tecnológica, MEC actúa en distintos roles respecto de los datos personales:',
        },
        {
          t: 'ul',
          items: [
            '**MEC es controlador de datos** respecto de: la información de cuenta del Usuario, los registros de transacciones al nivel de la Plataforma, los datos técnicos de uso de la Aplicación, los datos de seguridad y auditoría, y la información necesaria para el cumplimiento regulatorio que MEC debe conservar por obligación legal.',
            '**MEC actúa como procesador o facilitador** respecto de: los datos que las Agencias, los Comerciantes u otros proveedores independientes gestionan a través de sus propias aplicaciones (Mi Dashboard, Mi Admin, Mi Restaurante, Mi Cargo) en el ejercicio de sus operaciones. En esos casos, la Agencia o el Comerciante es el responsable primario de sus datos; MEC les provee el software y les da acceso a herramientas, pero el uso, la finalidad, y la relación con el titular de los datos son responsabilidad de la Agencia o del Comerciante.',
            'Los **servicios subyacentes** (envío físico, venta de productos, entrega, transporte) los prestan las Agencias, los Comerciantes, los transportistas externos y las tiendas externas. Cada uno de ellos puede tener su propia política de privacidad y sus propias prácticas de tratamiento de datos. MEC no controla ni es responsable de las prácticas independientes de esos terceros.',
          ],
        },
      ],
    },
    {
      id: 'seccion-2',
      number: '2',
      title: 'Qué información recopilamos',
      blocks: [
        { t: 'h3', text: '2.1 Del Remitente (usted)' },
        {
          t: 'ul',
          items: [
            'Nombre, fecha de nacimiento, dirección en EE. UU., correo electrónico y teléfono.',
            'Documento oficial de identidad (licencia de conducir, identificación estatal, pasaporte o identificación consular) e imagen del documento, recopilados a través de un proveedor de verificación de identidad.',
            'Información de pago, gestionada por un procesador externo autorizado.',
            'Historial de transacciones (envíos, pedidos del Marketplace, pre-avisos de Casillero Envíos, Compras Asistidas de Casillero Tienda).',
            'Información de soporte (correos, mensajes, reclamaciones).',
            'Información técnica de su dispositivo (identificadores del dispositivo, sistema operativo, dirección IP, tokens de notificaciones) y eventos de uso de la Aplicación.',
          ],
        },
        { t: 'h3', text: '2.2 Del destinatario en Cuba' },
        {
          t: 'ul',
          items: [
            'Nombre, carné de identidad (CI), dirección en Cuba (provincia, municipio, dirección detallada) y teléfono.',
            'Comprobante de entrega (firma, fotografía, sello de tiempo, ubicación de la entrega), capturado por la Agencia o el Comerciante al momento de la entrega.',
          ],
        },
        {
          t: 'p',
          text: 'El destinatario no es parte de los Términos, pero su información se protege con el mismo nivel de seguridad. La información se transmite a la Agencia o al Comerciante que ejecuta la entrega, y esos actores pueden ser co-responsables de la información conforme a la ley aplicable.',
        },
        { t: 'h3', text: '2.3 Generados específicamente por Casillero' },
        {
          t: 'p',
          text: '**Casillero Envíos:** nombre del transportista externo y número de seguimiento, descripción del contenido, peso estimado y valor declarado opcional, estado del paquete, y marca de tiempo y atribución de cada confirmación de llegada.',
        },
        {
          t: 'p',
          text: '**Casillero Tienda:** tienda externa donde se ejecuta la compra, descripción y costo de los bienes, costo de envío cobrado por la tienda, Tarifa de Servicio de Compra, modalidad de comprador (MEC o Agencia), estado de la Compra Asistida, marca de tiempo y atribución, y registro de su autorización expresa al mandato y de la confirmación de la pantalla de venta final.',
        },
      ],
    },
    {
      id: 'seccion-3',
      number: '3',
      title: 'Para qué la usamos',
      blocks: [
        { t: 'p', text: 'MEC utiliza la información recopilada para:' },
        {
          t: 'ul',
          items: [
            '**Operar la Plataforma:** mantener su cuenta, procesar las transacciones que usted inicia, comunicarle el estado, y proveer soporte técnico y de facturación al nivel de la Plataforma.',
            '**Facilitar los servicios subyacentes:** transmitir a la Agencia, al Comerciante, a la tienda externa o al transportista la información necesaria para que ejecuten el servicio que usted ha contratado con ellos.',
            '**Cumplimiento legal y regulatorio:** verificación contra listas de sanciones, conservación de registros, reportes requeridos por la ley aplicable, y cumplimiento de las obligaciones que apliquen a MEC como operador de la Plataforma.',
            '**Prevención de fraude y seguridad:** detección de patrones sospechosos, verificación de identidad, investigación de disputas y de incidentes de seguridad.',
            '**Comunicaciones de servicio (no opcionales):** confirmaciones de transacción, avisos de seguridad y de cumplimiento, actualizaciones legales, cambios materiales a estos Términos o a la Política.',
            '**Comunicaciones de marketing (opcionales):** novedades y ofertas, solo con su consentimiento. Puede darse de baja en cualquier momento.',
            '**Mejora del producto:** análisis de uso agregado y anónimo.',
          ],
        },
        { t: 'p', text: '**MEC no vende información personal.**' },
      ],
    },
    {
      id: 'seccion-4',
      number: '4',
      title: 'Con quién la compartimos',
      blocks: [
        { t: 'p', text: 'Compartimos información únicamente con:' },
        { t: 'h3', text: '4.1 Proveedores de servicio de MEC (procesadores)' },
        {
          t: 'p',
          text: 'Infraestructura en la nube, procesamiento de pagos, mensajería transaccional (SMS, correo), verificación de identidad, y verificación contra listas de sanciones. Estos proveedores procesan datos bajo obligaciones contractuales de confidencialidad y seguridad y se ubican principalmente en los Estados Unidos.',
        },
        { t: 'h3', text: '4.2 Agencias, Comerciantes y otros prestadores de servicios subyacentes' },
        {
          t: 'p',
          text: 'MEC transmite a la Agencia, al Comerciante o al proveedor correspondiente únicamente la información necesaria para procesar la transacción y prestar el servicio subyacente: nombre y datos de contacto del destinatario, contenido del envío o del pedido, dirección de entrega, comprobantes de entrega, y comunicaciones de coordinación.',
        },
        {
          t: 'p',
          text: 'Las Agencias y los Comerciantes pueden actuar como **controladores independientes o co-controladores** de los datos que reciban para el ejercicio de su propia operación, y tienen sus propias obligaciones de privacidad frente a los titulares de datos. MEC no controla las prácticas independientes de las Agencias o Comerciantes más allá de las obligaciones contractuales que MEC pueda haber establecido con ellos.',
        },
        { t: 'h3', text: '4.3 Tiendas Externas (Casillero Tienda)' },
        {
          t: 'p',
          text: 'En una Compra Asistida, se comparte con la tienda externa la información mínima necesaria para ejecutar la compra a su nombre (nombre del comprador, dirección de envío de la Agencia, información de pago según corresponda al método autorizado). Las prácticas de privacidad de cada tienda externa se rigen por su propia política.',
        },
        { t: 'h3', text: '4.4 Transportistas Externos (Casillero Envíos)' },
        {
          t: 'p',
          text: 'Cuando usted contrata directamente un transportista externo y nos proporciona el número de seguimiento, la Plataforma puede consultar al transportista externo el estado de la entrega. Las prácticas de privacidad del transportista externo se rigen por su propia política.',
        },
        { t: 'h3', text: '4.5 Autoridades' },
        {
          t: 'p',
          text: 'Compartimos información con autoridades competentes cuando la ley aplicable así lo requiera, o cuando tengamos motivos razonables para considerar que es necesario prevenir o investigar fraude, lavado de dinero, terrorismo o lesión a personas. Le informaremos antes de divulgar cuando la ley lo permita.',
        },
      ],
    },
    {
      id: 'seccion-5',
      number: '5',
      title: 'Cuánto tiempo la guardamos',
      blocks: [
        {
          t: 'p',
          text: 'Conservamos la información personal mientras su cuenta esté activa y durante los plazos requeridos por la ley aplicable. Los plazos típicos:',
        },
        {
          t: 'ul',
          items: [
            '**Registros de transacciones y de identidad:** al menos cinco (5) años tras la transacción, conforme a las obligaciones de conservación aplicables.',
            '**Comunicaciones de soporte:** dos (2) años desde la última comunicación, o el plazo legal aplicable cuando sea mayor.',
            '**Datos técnicos de uso:** hasta veinticuatro (24) meses desde la última sesión.',
          ],
        },
        {
          t: 'p',
          text: 'Tras el plazo aplicable, eliminamos o anonimizamos la información, salvo cuando exista una obligación legal de retención adicional.',
        },
        {
          t: 'p',
          text: 'Las Agencias, Comerciantes y otros terceros aplican sus propios plazos de retención a los datos que gestionan bajo su control.',
        },
      ],
    },
    {
      id: 'seccion-6',
      number: '6',
      title: 'Cómo la protegemos',
      blocks: [
        {
          t: 'p',
          text: 'MEC aplica medidas técnicas y organizativas razonables, conforme al estado de la técnica, para proteger su información: cifrado de datos sensibles, controles de acceso por roles, segregación lógica de datos entre clientes de la Plataforma, y revisiones periódicas de seguridad. Por razones operacionales no divulgamos públicamente la totalidad de las medidas específicas.',
        },
        {
          t: 'p',
          text: 'Si ocurre un incidente de seguridad que comprometa su información, le notificaremos sin demora indebida y, donde corresponda, también notificaremos a las autoridades reguladoras, conforme a la ley aplicable.',
        },
        {
          t: 'p',
          text: 'Ningún sistema es perfecto. MEC no garantiza que la información esté libre de toda intrusión, pero se compromete a las medidas descritas.',
        },
      ],
    },
    {
      id: 'seccion-7',
      number: '7',
      title: 'Transferencia de datos a Cuba',
      blocks: [
        {
          t: 'p',
          text: 'Para prestar los Servicios, MEC necesariamente transfiere información del destinatario (y, en menor medida, del Remitente) a Cuba, a las Agencias y Comerciantes que ejecutan la entrega. Esta transferencia se limita a la información necesaria para la entrega.',
        },
      ],
    },
    {
      id: 'seccion-8',
      number: '8',
      title: 'Sus derechos',
      blocks: [
        {
          t: 'p',
          text: 'Sujeto a las leyes aplicables y a las excepciones por retención legal obligatoria, usted puede solicitar:',
        },
        {
          t: 'ul',
          items: [
            '**Acceso** a la información que tenemos sobre usted.',
            '**Rectificación** de información inexacta.',
            '**Eliminación** de su información, sujeto a las excepciones de retención legal.',
            '**Portabilidad** de su información en un formato común.',
            '**Oposición o limitación** del tratamiento para fines de marketing en cualquier momento.',
          ],
        },
        {
          t: 'p',
          text: 'Para ejercer cualquier derecho, escriba al correo indicado en la Sección 1.1. Verificaremos razonablemente su identidad y responderemos dentro del plazo aplicable (típicamente 30–45 días).',
        },
        {
          t: 'p',
          text: 'Si sus derechos se relacionan con datos que están bajo el control principal de una Agencia o Comerciante (por ejemplo, historial de compras en un mercado específico de un Comerciante), podemos dirigir su solicitud a la Agencia o el Comerciante correspondiente. Le informaremos cuando así lo hagamos.',
        },
        {
          t: 'p',
          text: 'Si reside en California, Florida, u otra jurisdicción con leyes específicas de privacidad, consulte el **Aviso de Derechos de Privacidad Estatales** para los derechos adicionales que su ley local le confiere.',
        },
        {
          t: 'p',
          text: 'MEC **no vende** información personal en el sentido de la CCPA/CPRA, la Florida Digital Bill of Rights u otras leyes estatales aplicables.',
        },
      ],
    },
    {
      id: 'seccion-9',
      number: '9',
      title: 'Menores',
      blocks: [
        {
          t: 'p',
          text: 'Los Servicios están disponibles solo para personas mayores de 18 años. MEC no recopila intencionalmente información de menores de 13 años, conforme a la COPPA. Si descubrimos que recopilamos información de un menor de 13, la eliminaremos sin demora.',
        },
      ],
    },
    {
      id: 'seccion-10',
      number: '10',
      title: 'Cookies y tecnologías de seguimiento',
      blocks: [
        {
          t: 'p',
          text: 'En la Aplicación móvil usamos identificadores del dispositivo, tokens de notificaciones push y un SDK de analítica para medir uso agregado y errores. Respetamos las solicitudes “Do Not Track” del sistema operativo (App Tracking Transparency en iOS, Limit Ad Tracking en Android).',
        },
        {
          t: 'p',
          text: 'En mienviocuba.com usamos cookies estrictamente necesarias para el funcionamiento del sitio y cookies de analítica. En jurisdicciones que requieren consentimiento previo, las cookies no esenciales se activan solo tras consentimiento. Consulte la **Política de Cookies** para el detalle.',
        },
      ],
    },
    {
      id: 'seccion-11',
      number: '11',
      title: 'Cambios y contacto',
      blocks: [
        {
          t: 'p',
          text: 'MEC puede modificar esta Política. Los cambios materiales se notifican con al menos treinta (30) días de anticipación dentro de la Aplicación o por correo electrónico.',
        },
        {
          t: 'p',
          text: 'Esta Política se publica originalmente en español. Si publicamos una versión en inglés, el texto en español prevalecerá ante divergencia, salvo que la ley aplicable disponga lo contrario.',
        },
        {
          t: 'p',
          text: 'Para preguntas sobre esta Política o sobre el tratamiento de su información personal:',
        },
        {
          t: 'ul',
          items: ['**Correo:** support@mienviocuba.com', '**Teléfono:** +1 (786) 519-1709'],
        },
      ],
    },
  ],
};
